/**
 * SuccessorsColumnNode - A React Flow node that renders multiple successor cards
 * with an integrated gateway and connection lines for decision branching.
 * Measures actual card positions to calculate precise center for edge alignment.
 */
import { Handle, Position, useStore, type ReactFlowState } from '@xyflow/react';
import { memo, useRef, useLayoutEffect, useState } from 'react';
import { X } from 'lucide-react';
import { type EntitySuggestion, type User } from '@bliks-io/contracts';
import SuccessorCard, { type SuccessorCardType } from '../components/cards/SuccessorCard';
import type { SuccessorChangeHandler, SuccessorUpdateData } from '../types';

export interface SuccessorData {
  outputLabel?: string;
  value: Required<SuccessorUpdateData>;
  onChange: SuccessorChangeHandler;
  /** Optional rule id when this successor belongs to a decision rule. */
  sourceRuleId?: string;
}

export interface SuccessorsColumnNodeData {
  users: User[];
  successors: SuccessorData[];
  submitted?: boolean;
  type?: SuccessorCardType;
  /** Recording ID — required for the system-request flow inside the picker. */
  recordingId?: string;
  /** Process ID — drives the "for this process" picker bucket. */
  processId?: string;
  /** Entity suggestions on the recording — used to render ghost rows. */
  entitySuggestions?: EntitySuggestion[];
}

// Selector to get current zoom level from React Flow's transform
const zoomSelector = (state: ReactFlowState) => state.transform[2];

/**
 * Converts screen pixel measurements to logical pixels (zoom-independent).
 * getBoundingClientRect returns screen pixels which are scaled by zoom,
 * but CSS positioning uses logical pixels which are unscaled.
 */
function getLogicalCenterY(elementRect: DOMRect, containerRect: DOMRect, zoom: number): number {
  const relativeTop = (elementRect.top - containerRect.top) / zoom;
  const height = elementRect.height / zoom;
  return relativeTop + height / 2;
}

const SuccessorsColumnNode = ({ data }: { data: SuccessorsColumnNodeData }) => {
  const { users, successors, submitted, type, recordingId, processId, entitySuggestions } = data;
  const hasMultipleSuccessors = successors.length > 1;
  const cardType: SuccessorCardType = hasMultipleSuccessors ? 'decision' : type || 'task';
  const zoom = useStore(zoomSelector);

  // Refs to measure actual card positions
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const horizontalLineRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [centerOffset, setCenterOffset] = useState<number | null>(null);
  const [verticalLineOffsets, setVerticalLineOffsets] = useState<{
    top: number;
    height: number;
  } | null>(null);

  // Factory for creating bounded ref setters (prevents sparse arrays)
  const createBoundedRefSetter =
    (refArray: React.MutableRefObject<(HTMLDivElement | null)[]>) =>
    (index: number) =>
    (el: HTMLDivElement | null) => {
      if (index >= 0 && index < successors.length) {
        refArray.current[index] = el;
      }
    };

  const setCardRef = createBoundedRefSetter(cardRefs);
  const setHorizontalLineRef = createBoundedRefSetter(horizontalLineRefs);

  // Gateway dimensions for layout calculations
  const GATEWAY_SIZE = 36;
  const GATEWAY_LEFT_OFFSET = 22;

  useLayoutEffect(
    function measureCardPositions() {
      // Trim ref arrays to match current successors count
      cardRefs.current = cardRefs.current.slice(0, successors.length);
      horizontalLineRefs.current = horizontalLineRefs.current.slice(0, successors.length);

      if (!containerRef.current || !cardsContainerRef.current) return;

      // Verify all refs are assigned before measuring
      const validRefs = cardRefs.current.filter(Boolean);
      if (validRefs.length !== successors.length) return;

      // For multiple successors, also verify horizontal line refs are ready
      const validLineRefs = horizontalLineRefs.current.filter(Boolean);
      if (successors.length > 1 && validLineRefs.length !== successors.length) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const cardsContainerRect = cardsContainerRef.current.getBoundingClientRect();
      const currentZoom = zoom || 1;

      // Measure the vertical center of each card (in logical pixels)
      const cardCenters = cardRefs.current
        .filter((el): el is HTMLDivElement => el !== null)
        .map((el) => getLogicalCenterY(el.getBoundingClientRect(), containerRect, currentZoom));

      if (cardCenters.length === 0) return;

      // Calculate center for Handle and gateway positioning
      let calculatedCenter: number;
      if (cardCenters.length === 1) {
        calculatedCenter = cardCenters[0];
      } else if (cardCenters.length % 2 === 1) {
        const middleIndex = Math.floor(cardCenters.length / 2);
        calculatedCenter = cardCenters[middleIndex];
      } else {
        const lowerMiddleIndex = cardCenters.length / 2 - 1;
        const upperMiddleIndex = cardCenters.length / 2;
        calculatedCenter = (cardCenters[lowerMiddleIndex] + cardCenters[upperMiddleIndex]) / 2;
      }
      setCenterOffset(calculatedCenter);

      // Calculate vertical line offsets from horizontal line positions (in logical pixels)
      const lineCenters = horizontalLineRefs.current
        .filter((el): el is HTMLDivElement => el !== null)
        .map((el) =>
          getLogicalCenterY(el.getBoundingClientRect(), cardsContainerRect, currentZoom)
        );

      if (lineCenters.length > 1) {
        setVerticalLineOffsets({
          top: lineCenters[0],
          height: lineCenters[lineCenters.length - 1] - lineCenters[0]
        });
      }
    },
    [successors, zoom]
  );

  return (
    <div className="nopan relative" ref={containerRef}>
      {/* Target handle at the diamond's left vertex so the edge terminates at the tip */}
      <Handle
        id="target"
        type="target"
        position={Position.Left}
        style={{
          top: centerOffset !== null ? `${centerOffset}px` : '50%',
          left: hasMultipleSuccessors
            ? `${GATEWAY_LEFT_OFFSET - (GATEWAY_SIZE * Math.SQRT2) / 2}px`
            : '0px',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          width: '10px',
          height: '10px'
        }}
      />

      {/* Successor cards with vertical line and horizontal connection lines */}
      <div
        className="relative flex flex-col gap-5"
        ref={cardsContainerRef}
        style={{ marginLeft: hasMultipleSuccessors ? GATEWAY_LEFT_OFFSET : 0 }}
      >
        {/* Vertical line connecting all branches - uses height instead of bottom to be independent of container size */}
        {hasMultipleSuccessors && verticalLineOffsets && (
          <div
            className="absolute bg-surface-strong"
            style={{
              width: '2px',
              left: '0px',
              top: `${verticalLineOffsets.top}px`,
              height: `${verticalLineOffsets.height}px`
            }}
          />
        )}

        {successors.map((successor, index) => (
          <div key={index} ref={setCardRef(index)} className="flex items-center">
            {/* Horizontal line with label (only for multiple successors) */}
            {hasMultipleSuccessors && (
              <div className="relative flex items-center" style={{ width: '160px' }}>
                {/* Full-width horizontal line */}
                <div
                  ref={setHorizontalLineRef(index)}
                  className="absolute left-0 right-0 h-[2px] bg-surface-strong"
                />
                {/* Label positioned on top of line */}
                <div className="relative w-full flex items-center justify-center">
                  <span className="text-xs font-medium text-fg-secondary bg-white px-2 py-0.5 text-center leading-tight max-w-[120px]">
                    {successor.outputLabel || `Output ${index + 1}`}
                  </span>
                </div>
              </div>
            )}
            <SuccessorCard
              users={users}
              submitted={submitted || false}
              value={successor.value}
              onChange={successor.onChange}
              type={cardType}
              recordingId={recordingId}
              processId={processId}
              sourceRuleId={successor.sourceRuleId}
              entitySuggestions={entitySuggestions}
            />
          </div>
        ))}
      </div>

      {/* Gateway rendered after cards container so it paints on top of connection lines */}
      {hasMultipleSuccessors && centerOffset !== null && (
        <div
          className="absolute z-10"
          style={{
            left: GATEWAY_LEFT_OFFSET - GATEWAY_SIZE / 2,
            top: `${centerOffset}px`,
            transform: 'translateY(-50%)',
            width: GATEWAY_SIZE,
            height: GATEWAY_SIZE
          }}
        >
          <div className="absolute inset-0 rotate-45 border-2 border-line-strong bg-white" />
          <div className="absolute inset-0 flex items-center justify-center">
            <X className="w-4 h-4 text-fg-subtle" strokeWidth={2} />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(SuccessorsColumnNode);
